// src/composables/useChartRefresh.js

import { ref, computed, onBeforeUnmount } from 'vue'
import { useStore } from '@/composables/useStore'

/**
 * Acompanha a recarga de um gráfico do painel pelo status real da run
 * (tabela chart_refresh_runs), em vez de re-buscar o gráfico e adivinhar o
 * término comparando se o valor mudou — heurística que nunca encerrava quando
 * o recálculo produzia o mesmo número.
 *
 * @param {{
 *   refreshAction: string,   // action Vuex do POST de refresh (devolve run_id)
 *   onFinished?: Function     // chamado uma vez, quando a run conclui
 * }} options
 */
export function useChartRefresh({ refreshAction, onFinished }) {

    const store = useStore()

    const POLL_INTERVAL_MS = 3000
    // Teto absoluto: o gráfico de eficiência passa de 200k linhas e pode
    // demorar mais que isso. Melhor liberar a tela com um aviso do que deixar
    // o indicador girando indefinidamente.
    const POLL_MAX_MS = 5 * 60 * 1000
    const MAX_CONSECUTIVE_ERRORS = 3

    // idle | queued | running | failed
    const state = ref('idle')
    const runId = ref(null)
    const errorCode = ref(null)

    let timer = null
    let pollStartedAt = null
    let consecutiveErrors = 0

    const isBusy = computed(() => state.value === 'queued' || state.value === 'running')
    const hasFailed = computed(() => state.value === 'failed')

    const statusLabel = computed(() => {
        if (state.value === 'queued') return 'Na fila'
        if (state.value === 'running') return 'Atualizando'
        return null
    })

    const stop = () => {
        if (timer) {
            clearTimeout(timer)
            timer = null
        }
    }

    const schedule = (delay = POLL_INTERVAL_MS) => {
        stop()
        timer = setTimeout(tick, delay)
    }

    const settle = (nextState, code = null) => {
        stop()
        state.value = nextState
        errorCode.value = code
        runId.value = null
    }

    const tick = async () => {

        // Aba em segundo plano não precisa consultar; só reagenda.
        if (typeof document !== 'undefined' && document.hidden) {
            schedule()
            return
        }

        if (pollStartedAt && (Date.now() - pollStartedAt) > POLL_MAX_MS) {
            settle('failed', 'TIMEOUT')
            return
        }

        try {

            const r = await store.dispatch('chart/ActionGetChartRefreshRun', { id: runId.value })

            consecutiveErrors = 0

            const status = r.data ? r.data.status : null

            if (status === 'completed') {
                settle('idle')
                if (onFinished) onFinished()
                return
            }

            if (status === 'failed') {
                settle('failed', r.data.error_code)
                return
            }

            state.value = status || 'running'
            schedule()

        } catch (e) {

            consecutiveErrors++

            if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
                // Libera o botão em vez de deixá-lo travado em loading, que é
                // o que acontecia quando o polling antigo tomava erro de rede.
                settle('failed', 'NETWORK')
                return
            }

            schedule(POLL_INTERVAL_MS * (consecutiveErrors + 1))
        }
    }

    /** Assume uma run já em andamento, vinda do campo `refresh` do GET. */
    const adopt = (run) => {

        if (!run || !run.run_id || isBusy.value) return

        runId.value = run.run_id
        state.value = run.status || 'queued'
        errorCode.value = null
        pollStartedAt = Date.now()

        schedule()
    }

    /** Dispara a recarga. Sem efeito enquanto já houver uma em andamento. */
    const start = async () => {

        if (isBusy.value) return

        state.value = 'queued'
        errorCode.value = null
        pollStartedAt = Date.now()

        try {

            const r = await store.dispatch(refreshAction)

            // O backend deduplica: com uma run ativa para o mesmo recorte, ele
            // devolve a existente em vez de enfileirar outra.
            runId.value = r.data ? r.data.run_id : null

            if (!runId.value) {
                settle('failed', 'NO_RUN_ID')
                return
            }

            schedule()

        } catch (e) {
            settle('failed', 'DISPATCH')
        }
    }

    onBeforeUnmount(stop)

    return { state, runId, errorCode, isBusy, hasFailed, statusLabel, start, adopt, stop }
}
